/**
 * Trilha e efeitos.
 *
 * O navegador só libera som depois de um gesto do usuário, e um jogo que fica
 * mudo até alguém descobrir por quê parece quebrado. Por isso a trilha só
 * começa quando o jogador toca em alguma coisa, e o estado disso é visível na
 * interface em vez de silencioso.
 */

import { ASSETS_BASE, type Manifest } from '../assets/manifest.js';

/**
 * Efeitos do acervo, pelo papel que cumprem na interface.
 *
 * A escolha é por duração, e não por nome. `Cursor1` tem 28 milissegundos: é
 * curto demais para ter envelope e sai como um estalo seco, repetido a cada
 * casa que o cursor anda. `Cursor3` tem 107 e soa como um toque. `Decision1`
 * vai a dois segundos e meio e ainda estava tocando quando o menu seguinte
 * abria; `Decision2` resolve em um quarto de segundo.
 */
export const SE = {
  cursor: 'Cursor3',
  confirma: 'Decision2',
  cancela: 'Cancel1',
  erro: 'Buzzer1',
  golpe: 'Sword2',
  acerto: 'Damage1',
  falha: 'Miss',
  critico: 'Slash2',
  morte: 'Fall',
  cura: 'Heal1',
  nivel: 'Up1',
  vitoria: 'Applause1',
  passo: 'Move1',
  teleporte: 'Flash1',
  turno: 'Wind1',
} as const;

export type SoundName = keyof typeof SE;

/**
 * Volume por efeito.
 *
 * O que se repete precisa ser mais baixo do que o que acontece uma vez: o
 * cursor toca dezenas de vezes por turno e o golpe crítico, uma. No mesmo
 * volume, o cursor é o que se ouve.
 */
const VOLUME: Partial<Record<SoundName, number>> = {
  cursor: 0.3,
  confirma: 0.45,
  cancela: 0.4,
  passo: 0.22,
  teleporte: 0.5,
  turno: 0.55,
};

const DEFAULT_VOLUME = 0.6;

/**
 * Intervalo mínimo entre duas repetições do mesmo efeito, em milissegundos.
 *
 * O passo é o caso que exige isto: a amostra do acervo dura 800 ms e a unidade
 * anda uma casa a cada 150. Sem trava, cinco cópias tocam sobrepostas e o que
 * se ouve não é passo — é ruído contínuo, que foi como isto soou até aqui.
 */
const MIN_GAP_MS: Partial<Record<SoundName, number>> = {
  passo: 200,
  cursor: 55,
};

/**
 * O gesto que liberou o som é da PÁGINA, não deste objeto.
 *
 * O navegador libera o áudio de uma aba inteira no primeiro gesto e não volta
 * atrás. Guardar isso por instância fazia o jogo emudecer ao sair da
 * escaramuça e voltar: o `GameAudio` novo nascia travado e ficava esperando um
 * gesto que a página já tinha recebido há muito tempo.
 */
let pageUnlocked = false;

export class GameAudio {
  #manifest: Manifest;
  #bgm: HTMLAudioElement | null = null;
  #currentMapBgm: string | null = null;
  #cache = new Map<string, HTMLAudioElement>();
  #lastPlayed = new Map<SoundName, number>();
  #muted = false;

  constructor(manifest: Manifest) {
    this.#manifest = manifest;
  }

  get unlocked(): boolean {
    return pageUnlocked;
  }

  get muted(): boolean {
    return this.#muted;
  }

  /** Chamado no primeiro gesto do jogador. */
  unlock(): void {
    pageUnlocked = true;
    void this.#bgm?.play().catch(() => undefined);
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    if (this.#bgm) this.#bgm.muted = muted;
  }

  #source(group: string, name: string): string | null {
    const entry = this.#manifest.audio[group]?.[name];
    if (!entry) return null;
    // Ogg primeiro por ser menor; o navegador que não o toca cai no AAC
    // sozinho, e canPlayType decide isso sem download.
    const probe = document.createElement('audio');
    if (entry.m4a && probe.canPlayType('audio/ogg; codecs=vorbis') === '') {
      return `${ASSETS_BASE}/audio/${group}/${entry.m4a}`;
    }
    return `${ASSETS_BASE}/audio/${group}/${entry.ogg}`;
  }

  playBgm(name: string): void {
    this.#currentMapBgm = name;
    const src = this.#source('bgm', name);
    if (!src) return;
    if (this.#bgm?.src.endsWith(src)) return;

    this.#bgm?.pause();
    const audio = new Audio(src);
    audio.loop = true;
    audio.volume = 0.4;
    audio.muted = this.#muted;
    this.#bgm = audio;
    if (pageUnlocked) void audio.play().catch(() => undefined);
  }

  /** Toca a música própria de batalha e salva a do mapa para retorno posterior. */
  playBattleBgm(isBoss = false): void {
    const battleTrack = isBoss ? 'Battle2' : 'Battle1';
    const src = this.#source('bgm', battleTrack);
    if (!src) return;
    if (this.#bgm?.src.endsWith(src)) return;

    this.#bgm?.pause();
    const audio = new Audio(src);
    audio.loop = true;
    audio.volume = 0.45;
    audio.muted = this.#muted;
    this.#bgm = audio;
    if (pageUnlocked) void audio.play().catch(() => undefined);
  }

  /** Restaura a música do mapa salva anteriormente. */
  restoreMapBgm(): void {
    if (this.#currentMapBgm) {
      this.playBgm(this.#currentMapBgm);
    } else {
      this.stopBgm();
    }
  }

  stopBgm(): void {
    this.#bgm?.pause();
    this.#bgm = null;
  }

  play(sound: SoundName): void {
    if (!pageUnlocked || this.#muted) return;

    const gap = MIN_GAP_MS[sound];
    if (gap !== undefined) {
      const now = performance.now();
      if (now - (this.#lastPlayed.get(sound) ?? -Infinity) < gap) return;
      this.#lastPlayed.set(sound, now);
    }

    const name = SE[sound];
    const src = this.#source('se', name);
    if (!src) return;

    let template = this.#cache.get(name);
    if (!template) {
      template = new Audio(src);
      this.#cache.set(name, template);
    }
    // Clonar deixa dois efeitos soarem juntos; reusar o mesmo elemento cortaria
    // o anterior, e num combate isso engole metade dos sons. O volume vem do
    // papel, e não do arquivo: dois papéis podem compartilhar a mesma amostra.
    const voice = template.cloneNode() as HTMLAudioElement;
    voice.volume = VOLUME[sound] ?? DEFAULT_VOLUME;
    void voice.play().catch(() => undefined);
  }

  destroy(): void {
    this.stopBgm();
    this.#cache.clear();
    this.#lastPlayed.clear();
  }
}
